import React, { useRef } from 'react';
import { useMantineColorScheme } from '@mantine/core';
import Editor from '@monaco-editor/react';

export interface ScimMonacoEditorProps {
    language?: string;
    value: string;
    onChange?: (value: string | undefined) => void;
    readOnly?: boolean;
    height?: string;
    width?: string;
    fontSize?: number;
    showLineNumbers?: boolean;
    showMinimap?: boolean;
    wordWrap?: 'on' | 'off' | 'wordWrapColumn' | 'bounded';
    onLoad?: (editor: any) => void | Promise<void>;
    options?: Record<string, any>;
    className?: string;
}

export const ScimMonacoEditor: React.FC<ScimMonacoEditorProps> = ({
    language = 'html',
    value,
    onChange,
    readOnly = false,
    height = '400px',
    width = '100%',
    fontSize = 14,
    showLineNumbers = true,
    showMinimap = false,
    wordWrap = 'on',
    onLoad,
    options = {},
    className
}) => {
    const { colorScheme } = useMantineColorScheme();
    const editorRef = useRef<any>(null);

    const monacoTheme = colorScheme === 'dark' ? 'vs-dark' : 'vs';

    const defaultOptions: Record<string, any> = {
        readOnly,
        fontSize,
        lineNumbers: showLineNumbers ? 'on' : 'off',
        minimap: { enabled: showMinimap },
        wordWrap,
        automaticLayout: true,
        scrollBeyondLastLine: false,
        tabSize: 2,
        insertSpaces: true,
        formatOnPaste: true,
        formatOnType: true,
        ...options
    };

    const handleEditorDidMount = async (editor: any, _monaco: any) => {
        editorRef.current = editor;
        if (onLoad) {
            await onLoad(editor);
        }
    };

    const handleChange = (value: string | undefined) => {
        if (onChange) {
            onChange(value);
        }
    };

    return (
        <Editor
            language={language}
            theme={monacoTheme}
            value={value}
            onChange={handleChange}
            height={height}
            width={width}
            options={defaultOptions}
            onMount={handleEditorDidMount}
            loading={<div>Loading editor...</div>}
            className={className}
        />
    );
};
